import { CurrencyCodes } from '@onevesthq/ov-enums';
import { useDebouncedValue } from '@utils/useDebouncedValue';
import { useMemo } from 'react';
import { useExchangeRate } from './useExchangeRate';
import {
  FEE_BASE_CURRENCY,
  FEE_QUERY_DEBOUNCE_MS,
  FULL_WITHDRAWAL_THRESHOLD,
} from './withdrawal.constants';
import {
  buildCalculateWithdrawFeesInput,
  resolveRequestedAmountCents,
} from './withdrawalMode.utils';

export type UseWithdrawalFeeRequestProp = {
  accountId: string;
  organizationId: string | undefined;
  bankAccountCurrency: CurrencyCodes | undefined;
  availableToWithdrawCents: number;
  withdrawAmountCents: number;
  isNetWithdrawal: boolean;
  netAmountCents: number | undefined;
};

export const useWithdrawalFeeRequest = ({
  accountId,
  organizationId,
  bankAccountCurrency,
  availableToWithdrawCents,
  withdrawAmountCents,
  isNetWithdrawal,
  netAmountCents,
}: UseWithdrawalFeeRequestProp) => {
  const {
    rate,
    isReady,
    isSettled: isFxRateSettled,
    hasRealRate,
    convertToBaseCurrency,
    convertFromBaseCurrency,
  } = useExchangeRate({
    organizationId,
    targetCurrency: bankAccountCurrency,
    baseCurrency: FEE_BASE_CURRENCY,
  });

  const requestedAmountCents = resolveRequestedAmountCents({
    isNetWithdrawal,
    netAmountCents,
    grossAmountCents: withdrawAmountCents,
  });
  const debouncedRequestedAmountCents = useDebouncedValue(
    requestedAmountCents,
    FEE_QUERY_DEBOUNCE_MS,
  );
  // Only the net amount is typed as a figure the adviser may still be entering; gross queries as before.
  const queriedAmountCents = isNetWithdrawal
    ? debouncedRequestedAmountCents
    : requestedAmountCents;
  // A mode or amount change leaves the debounced amount pairing the old value with the new mode.
  const isDebouncing =
    isNetWithdrawal && debouncedRequestedAmountCents !== requestedAmountCents;

  const amountInCadCents = useMemo(
    () => (isReady ? convertToBaseCurrency(queriedAmountCents) : 0),
    [queriedAmountCents, isReady, convertToBaseCurrency],
  );

  const isFxRateReady =
    !bankAccountCurrency ||
    bankAccountCurrency === CurrencyCodes.CAD ||
    hasRealRate;

  return {
    rate,
    hasRealRate,
    convertFromBaseCurrency,
    isFxRateReady,
    isFxRateSettled,
    isDebouncing,
    queriedAmountCents,
    variables: {
      input: buildCalculateWithdrawFeesInput({
        accountId,
        amountCents: amountInCadCents,
        isNetWithdrawal,
        isFullWithdrawal:
          queriedAmountCents >=
          availableToWithdrawCents * FULL_WITHDRAWAL_THRESHOLD,
      }),
    },
    skip:
      !accountId ||
      !isReady ||
      amountInCadCents <= 0 ||
      isDebouncing ||
      (isNetWithdrawal && !isFxRateReady),
  };
};
